import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { FileSignature, User, Briefcase, Calendar, PenTool, Info, CheckCircle2, AlertCircle, ShieldCheck } from "lucide-react";
import { generateSignatureFormat, validateSignature } from "../../../utils/formValidators";

export default function Step5Declaration({ formData, setFormData, errors, clientName, isAgentRegistration }) {
  const [signatureStatus, setSignatureStatus] = useState(null);
  const [expectedSignature, setExpectedSignature] = useState("");

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "applicantName") {
      const format = generateSignatureFormat(value);
      setExpectedSignature(format);
      if (formData.applicantSignature) {
        const validation = validateSignature(formData.applicantSignature, value);
        setSignatureStatus(validation.isValid ? "valid" : "invalid");
      }
    }
  };

  const handleSignatureChange = (value) => {
    handleChange("applicantSignature", value);
    if (formData.applicantName && value) {
      const validation = validateSignature(value, formData.applicantName);
      setSignatureStatus(validation.isValid ? "valid" : "invalid");
    } else {
      setSignatureStatus(null);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!formData.declarationDate) {
      setFormData((prev) => ({ ...prev, declarationDate: getTodayDate() }));
    }
    if (formData.applicantName) {
      setExpectedSignature(generateSignatureFormat(formData.applicantName));
    }
  }, []);

  const err = (f) => (errors?.[f] ? "border-red-300 bg-red-50" : "border-gray-300 hover:border-gray-400");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="p-8 bg-white rounded-xl border border-gray-200 shadow-sm"
    >
      <div className="flex gap-3 items-center mb-8">
        <div className="flex justify-center items-center w-10 h-10 text-white bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md">
          <FileSignature size={20} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Declaration</h2>
      </div>

      {isAgentRegistration && clientName && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex gap-3 items-start p-4 mb-8 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200"
        >
          <ShieldCheck size={24} className="flex-shrink-0 text-purple-600" />
          <div>
            <p className="text-sm font-semibold text-purple-900">Consent acknowledged</p>
            <p className="mt-1 text-sm text-purple-700">
              You are registering on behalf of <strong>{clientName}</strong>. Consent is assumed for this registration. Please confirm the applicant and signatory details below.
            </p>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-6">
          <div>
            <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
              <User size={16} className="text-purple-600" /> Name of Applicant <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.applicantName || ""}
              onChange={(e) => handleChange("applicantName", e.target.value)}
              className={"w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + err("applicantName")}
              placeholder="Enter applicant name"
            />
            {errors?.applicantName && <p className="mt-1 text-sm text-red-500">{errors.applicantName}</p>}
          </div>
          <div>
            <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
              <Briefcase size={16} className="text-purple-600" /> Designation of Signatory <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.signatoryDesignation || ""}
              onChange={(e) => handleChange("signatoryDesignation", e.target.value)}
              className={"w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + err("signatoryDesignation")}
              placeholder="Enter designation"
            />
            {errors?.signatoryDesignation && <p className="mt-1 text-sm text-red-500">{errors.signatoryDesignation}</p>}
          </div>
        </div>
        <div className="space-y-6">
          <div>
            <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
              <PenTool size={16} className="text-purple-600" /> Signature of Applicant (initials) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.applicantSignature || ""}
                onChange={(e) => handleSignatureChange(e.target.value)}
                disabled={!formData.applicantName}
                className={"w-full px-4 py-3 pr-10 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + (signatureStatus === "valid" ? "border-green-300 bg-green-50" : signatureStatus === "invalid" ? "border-red-300 bg-red-50" : err("applicantSignature")) + (!formData.applicantName ? " opacity-50 cursor-not-allowed" : "")}
                placeholder={formData.applicantName ? `e.g. ${expectedSignature || "J.D."}` : "Enter applicant name first"}
              />
              {signatureStatus === "valid" && <CheckCircle2 className="absolute top-3.5 right-3 text-green-500" size={20} />}
              {signatureStatus === "invalid" && <AlertCircle className="absolute top-3.5 right-3 text-red-500" size={20} />}
            </div>
            {formData.applicantName && expectedSignature && (
              <div className="flex gap-2 items-start p-3 mt-2 bg-purple-50 rounded-lg border border-purple-200">
                <Info size={16} className="flex-shrink-0 mt-0.5 text-purple-600" />
                <div>
                  <p className="text-xs font-semibold text-purple-900">Expected format</p>
                  <p className="mt-1 text-sm font-bold text-purple-700">{expectedSignature}</p>
                </div>
              </div>
            )}
            {errors?.applicantSignature && <p className="mt-1 text-sm text-red-500">{errors.applicantSignature}</p>}
          </div>
          <div>
            <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
              <Calendar size={16} className="text-purple-600" /> Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.declarationDate || getTodayDate()}
              onChange={(e) => handleChange("declarationDate", e.target.value)}
              className={"w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + err("declarationDate")}
            />
            {errors?.declarationDate && <p className="mt-1 text-sm text-red-500">{errors.declarationDate}</p>}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
